import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { config } from "../../config";

interface OrganizationForm {
  name: string;
  slug: string;
  subdomain: string;
  domains: string;
  allowPassword: boolean;
  allowGoogle: boolean;
  allowMicrosoft: boolean;
  ssoOnly: boolean;
  mfaRequired: boolean;
}

export function CreateOrganizationPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<OrganizationForm>({
    name: "",
    slug: "",
    subdomain: "",
    domains: "",
    allowPassword: true,
    allowGoogle: true,
    allowMicrosoft: false,
    ssoOnly: false,
    mfaRequired: false,
  });

  const handleChange = (field: keyof OrganizationForm, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));

    // Auto-generate slug from name
    if (field === "name" && typeof value === "string") {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      setForm((prev) => ({ ...prev, slug }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const domains = form.domains
        .split(",")
        .map((d) => d.trim().toLowerCase())
        .filter((d) => d.length > 0);

      const response = await fetch(`${config.apiBaseUrl}/v1/admin/organizations`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: form.name,
          slug: form.slug,
          subdomain: form.subdomain || undefined,
          domains: domains.length > 0 ? domains : undefined,
          settings: {
            allowPassword: form.allowPassword,
            allowGoogle: form.allowGoogle,
            allowMicrosoft: form.allowMicrosoft,
            ssoOnly: form.ssoOnly,
            mfaRequired: form.mfaRequired,
          },
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error ?? "Failed to create organization");
      }

      const data = await response.json();
      navigate(`/admin/organizations/${data.organization.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Create Organization</h1>
      </header>

      <form onSubmit={handleSubmit} className="admin-form">
        {error && <div className="form-error">{error}</div>}

        <div className="form-section">
          <h2>Basic Information</h2>

          <div className="form-group">
            <label htmlFor="name">Organization Name *</label>
            <input
              id="name"
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Acme Inc"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="slug">Slug *</label>
            <input
              id="slug"
              type="text"
              value={form.slug}
              onChange={(e) => handleChange("slug", e.target.value)}
              placeholder="acme"
              pattern="[a-z0-9-]+"
              required
            />
            <small>Lowercase letters, numbers, and hyphens only. Used as organization ID.</small>
          </div>

          <div className="form-group">
            <label htmlFor="subdomain">Subdomain (optional)</label>
            <input
              id="subdomain"
              type="text"
              value={form.subdomain}
              onChange={(e) => handleChange("subdomain", e.target.value)}
              placeholder="acme"
            />
            <small>
              If set, users can access via {form.subdomain || "subdomain"}.agentech-dev.com
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="domains">Email Domains (optional)</label>
            <input
              id="domains"
              type="text"
              value={form.domains}
              onChange={(e) => handleChange("domains", e.target.value)}
              placeholder="acme.com, acme.io"
            />
            <small>Comma-separated list of email domains for this organization.</small>
          </div>
        </div>

        <div className="form-section">
          <h2>Authentication Settings</h2>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={form.allowPassword}
                onChange={(e) => handleChange("allowPassword", e.target.checked)}
              />
              Allow Password Login
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={form.allowGoogle}
                onChange={(e) => handleChange("allowGoogle", e.target.checked)}
              />
              Allow Google SSO
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={form.allowMicrosoft}
                onChange={(e) => handleChange("allowMicrosoft", e.target.checked)}
              />
              Allow Microsoft SSO
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={form.ssoOnly}
                onChange={(e) => handleChange("ssoOnly", e.target.checked)}
              />
              SSO Only (disable password login for this org)
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={form.mfaRequired}
                onChange={(e) => handleChange("mfaRequired", e.target.checked)}
              />
              Require MFA
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate("/admin/organizations")}
            className="btn btn-secondary"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Organization"}
          </button>
        </div>
      </form>
    </div>
  );
}
